import {
  Box,
  Typography as Typo,
  Paper,
  useTheme,
  styled,
  TypographyProps,
} from '@mui/material';
import {Notification} from '@jobseeker/lotus/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {colorPalette} from '../../foundations/colors';
import type {NotificationComponentProps} from './types';
import Divider from '@jobseeker/lotus/components/Divider';
import {display} from '@mui/system';
import {getIconClassType} from '@jobseeker/lotus/helpers/notificationIcon';
import {TrackEventUBA} from '@jobseeker/common-core/utils/analytics';
import {getKeyFromPath} from '@jobseeker/common-core/utils';
import {useEffect} from 'react';

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

type NotificationlistPaperProps = {
  isRead: boolean;
  isOldView: boolean;
};

const NotificationlistPaper = styled(Paper)<NotificationlistPaperProps>(
  ({theme, isRead, isOldView}) => ({
    borderRadius: 0,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: isRead
      ? '#fff'
      : isOldView
      ? colorPalette.green.A601
      : '#fff',
    gap: theme.spacing(1.5),
    '.joblist__logo': {
      borderRadius: theme.spacing(1.5),
      border: '0.6px solid #7E7E7E33',
    },
    '.joblist__right_element': {
      position: 'absolute',
      width: 'fit-content',
      right: theme.spacing(2.5),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      textAlign: 'right',
      padding: theme.spacing(2),
      top: 0,
      bottom: 0,
    },
    '.joblist__subtitle': {
      p: {
        wordBreak: 'break-all',
        color: theme.palette.text.secondary,
      },
    },
    '.save-icon': {
      display: 'none',
    },
    ':hover': {
      '.save-icon': {
        display: 'unset',
      },
    },
  })
);

const NotificationComponent = ({
  id,
  message,
  redirectTo,
  notificationSpan,
  isRead,
  status,
  type,
  handleNotificationItemClick,
  isOldView = false,
}: NotificationComponentProps) => {
  const theme = useTheme();
  // onClick={
  //   isOldView
  //     ? () => handleNotificationItemClick(id, status, type, redirectTo)
  //     : undefined
  // }
  
  const isStatusChangeParams = type === 2;

  const onNotificationClickRaw = () => {
    TrackEventUBA('NotificationClick', 'click', {
      ref: getKeyFromPath('ref') || '',
      subtype: isStatusChangeParams ? 'status change notification' : '',
    });
    handleNotificationItemClick(id, status, type, redirectTo);
  };
  
  return (
    <>
      <Box
        onClick={
          isOldView
            ? onNotificationClickRaw
            : () => undefined
        }
        color="gray.dark"
        data-testid="notification_list_item"
        sx={{
          position: 'relative',
          transition: 'all ease 0.2s',
          cursor: isOldView ? 'pointer' : '',
          marginBottom: !isOldView ? '10px' : '',
          borderBottom: !isOldView ? 'none' : 'solid 1px',
          borderColor: !isOldView ? 'transparent' : 'border.lightGray',
          ':hover': !isOldView
            ? {}
            : {
                boxShadow: '6px 6px 14px 3px rgba(19, 21, 23, 0.04)',
                zIndex: 2,
                borderColor: isRead
                  ? '#fff'
                  : isOldView
                  ? colorPalette.green.A601
                  : '#fff',
              },
        }}
      >
        <Box>
          <NotificationlistPaper
            onClick={
              isOldView
                ? () => undefined
                : onNotificationClickRaw
            }
            key={`noti_${id}`}
            elevation={0}
            isRead={isRead}
            isOldView={isOldView}
            sx={{
              py: {xxs: 1.5, md: isOldView ? 2 : 0.5},
              px: {xxs: 1.5, md: 2},
              mr: isOldView ? 0 : {md: 4, xxs: 0},
              borderRadius: !isOldView ? '10px' : '',
              display: 'flex',
              cursor: isOldView ? '' : 'pointer',
            }}
          >
            <Box
              onClick={
                isOldView
                  ? () => undefined
                  : onNotificationClickRaw
              }
              sx={{
                display: !isOldView ? 'flex' : 'flex',
                alignSelf: !isOldView ? 'flex-start' : 'initial',
              }}
            >
              <Box
                className="joblist__logo"
                sx={{
                  p: !isOldView ? theme.spacing(2.0) : theme.spacing(1.5),
                  height: !isOldView ? '50px' : 'min-content',
                  width: !isOldView ? '50px' : '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: isRead
                    ? isOldView
                      ? '#fff'
                      : getIconClassType(type).color
                    : isOldView
                    ? colorPalette.green.A601
                    : getIconClassType(type).color,
                  boxShadow: !isOldView
                    ? '0px 4px 10px rgba(0, 0, 0, 0.2)'
                    : '',
                  borderRadius: !isOldView ? '10px' : undefined,
                  overflow: !isOldView ? 'visible' : '',
                  marginLeft: !isOldView ? '-40px' : '',
                  ...(!isOldView && {marginTop: '5px', marginBottom: '5px'}),
                }}
              >
                {!isOldView && (
                  <Image
                    src={getIconClassType(type).icon}
                    alt={'Logo'}
                    width={20}
                    height={29}
                    quality={50}
                    style={{
                      objectFit: 'contain',
                    }}
                  />
                )}
                {isOldView && (
                  <Image
                    src={Notification}
                    alt={'Logo'}
                    width="25"
                    height="25"
                    quality={50}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isOldView && (
                <Box
                  sx={{
                    width: '100%',
                    mt: 0.5,
                    display: {xxs: 'flex', md: 'none'},
                    justifyContent: 'flex-start',
                  }}
                >
                  <Typography
                    component="span"
                    color="gray.dark"
                    variant="caption"
                    fontSize={'12px'}
                    lineHeight={'16px'}
                    fontWeight={'300'}
                  >
                    {notificationSpan}
                  </Typography>
                </Box>
              )}
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                gap={2}
                alignItems={'center'}
              >
                <Typography
                  component={'p'}
                  data-testid="message_title"
                  fontSize={isOldView ? undefined : '15px'}
                  variant="body2"
                  lineHeight="21px"
                  width={'90%'}
                  dangerouslySetInnerHTML={{
                    __html: isOldView
                      ? message
                      : type === 4 || type === 5
                      ? `${
                          message.split('Click')[0]
                        }<br /><span style="text-decoration: underline; color: ${theme.palette.primary.main};">Click${
                          message.split('Click')[1]
                        }</span>`
                      : message,
                  }}
                  sx={{
                    lineClamp: '3',
                    '-webkit-line-clamp': '2',
                    '-webkit-box-orient': 'vertical',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    display: 'block',
                    alignItems: 'center',
                  }}
                />
                <Box display={'flex'} justifyContent={'space-between'}>
                  {!isOldView && (
                    <Divider
                      orientation="vertical"
                      sx={{
                        display: {xxs: 'none', md: 'flex'},
                        height: '30px',
                        borderColor: '#e9effa',
                        borderWidth: '0.25px',
                        borderStyle: 'solid',
                        width: '0.25px',
                        alignSelf: 'center',
                        marginX: '17px',
                      }}
                    />
                  )}

                  {!isOldView && status !== 2 && (
                    <Box
                      sx={{
                        color: '#ffffff',
                        borderRadius: '3px',
                        backgroundColor: '#49c5b6',
                        lineHeight: '14px',
                        fontSize: '10px',
                        fontWeight: 500,
                        padding: '2px 4px',
                        position: 'absolute',
                        left: {xs: '37px', md: '40px'},
                        top: '-8px',
                        display: 'inline-block',
                      }}
                    >
                      NEW
                    </Box>
                  )}

                  {!isOldView && (
                    <Typography
                      component={'span'}
                      color="gray.dark"
                      data-testid="notification_span"
                      variant="caption"
                      display={{xxs: 'none', md: 'flex'}}
                      minWidth={'100px'}
                      alignItems={'center'}
                      justifyContent={'center'}
                      ml={1}
                      sx={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        fontWeight: '300',
                        width: '80px',
                        minHeight: '48px',
                        verticalAlign: 'middle',
                        display: 'inline-block',
                      }}
                    >
                      {notificationSpan}
                    </Typography>
                  )}
                  {isOldView && (
                    <Typography
                      component={'span'}
                      color="text.secondary"
                      data-testid="notification_span"
                      variant="caption"
                      display={{xxs: 'none', md: 'flex'}}
                      minWidth={'100px'}
                      alignItems={'center'}
                      justifyContent={'flex-end'}
                      ml={1}
                    >
                      {notificationSpan}
                    </Typography>
                  )}
                </Box>
              </Box>

              {isOldView && (
                <Box
                  sx={{
                    width: '100%',
                    mt: 1,
                    display: {xxs: 'flex', md: 'none'},
                  }}
                >
                  <Typography
                    component={'span'}
                    color="text.secondary"
                    variant="caption"
                  >
                    {notificationSpan}
                  </Typography>
                </Box>
              )}
            </Box>
          </NotificationlistPaper>
        </Box>
      </Box>
    </>
  );
};

export default NotificationComponent;
